import type { GatewayBrowserClient } from "../gateway.ts";
import type { SessionLogEntry } from "../views/usage.ts";
import { resolveAgentIdForSession } from "../chat/resolve-session-agent.ts";

export type SessionArtifactRecord = {
  id: string;
  ts: number;
  sessionKey: string;
  agentId: string;
  tool: string;
  path: string;
  op: "created" | "modified" | "deleted" | "manual";
  shaBefore?: string;
  shaAfter?: string;
  beforeBytes?: number;
  afterBytes?: number;
  projectId?: string;
};

export type SessionArtifactDetail = {
  change: SessionArtifactRecord;
  before: string | null;
  after: string | null;
  diff: string;
  beforeTruncated: boolean;
  afterTruncated: boolean;
};

export type SessionArtifactsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  /** Workspace/artifact scope (parent or child subagent session). */
  filesScopeSessionKey?: string;
  chatActiveProjectId?: string | null;
  sessionArtifactsLoading?: boolean;
  sessionArtifactsError?: string | null;
  sessionArtifacts?: SessionArtifactRecord[];
  sessionArtifactDetailLoading?: boolean;
  sessionArtifactDetail?: SessionArtifactDetail | null;
  sessionArtifactSelectedId?: string | null;
  sessionArtifactWriteSaving?: boolean;
  sessionInspectorLogs?: SessionLogEntry[] | null;
  sessionInspectorLogsLoading?: boolean;
};

export async function loadInspectorSessionLogs(state: SessionArtifactsState): Promise<void> {
  if (!state.client || !state.connected || !state.sessionKey.trim()) {
    return;
  }
  state.sessionInspectorLogsLoading = true;
  state.sessionInspectorLogs = null;
  try {
    const res = await state.client.request<{ logs: SessionLogEntry[] }>("sessions.usage.logs", {
      key: state.sessionKey,
      limit: 200,
    });
    state.sessionInspectorLogs = res?.logs ?? [];
  } catch {
    state.sessionInspectorLogs = [];
  } finally {
    state.sessionInspectorLogsLoading = false;
  }
}

export async function loadSessionArtifacts(state: SessionArtifactsState): Promise<void> {
  if (!state.client || !state.connected || !state.sessionKey.trim()) {
    return;
  }
  state.sessionArtifactsLoading = true;
  state.sessionArtifactsError = null;
  try {
    const scopeKey = state.filesScopeSessionKey?.trim() || state.sessionKey;
    const agentId = resolveAgentIdForSession(scopeKey);
    const res = await state.client.request<{
      changes: SessionArtifactRecord[];
      truncated: boolean;
    }>("sessions.artifacts.list", {
      agentId,
      sessionKey: scopeKey,
      ...(state.chatActiveProjectId ? { projectId: state.chatActiveProjectId } : {}),
      limit: 100,
    });
    state.sessionArtifacts = res?.changes ?? [];
  } catch (err) {
    state.sessionArtifactsError = String(err);
    state.sessionArtifacts = [];
  } finally {
    state.sessionArtifactsLoading = false;
  }
}

export async function loadSessionArtifactDetail(
  state: SessionArtifactsState,
  changeId: string,
  scopeSessionKey?: string,
): Promise<boolean> {
  if (!state.client || !state.connected || !changeId.trim()) {
    return false;
  }
  const scopeKey =
    scopeSessionKey?.trim() || state.filesScopeSessionKey?.trim() || state.sessionKey.trim();
  if (!scopeKey) {
    return false;
  }
  state.sessionArtifactSelectedId = changeId;
  state.sessionArtifactDetailLoading = true;
  state.sessionArtifactsError = null;
  try {
    const agentId = resolveAgentIdForSession(scopeKey);
    const res = await state.client.request<SessionArtifactDetail>("sessions.artifacts.get", {
      agentId,
      sessionKey: scopeKey,
      changeId,
      ...(state.chatActiveProjectId ? { projectId: state.chatActiveProjectId } : {}),
    });
    if (res?.change) {
      state.sessionArtifactDetail = res;
      state.filesScopeSessionKey = scopeKey;
      return true;
    }
    state.sessionArtifactDetail = null;
    state.sessionArtifactsError = "Change not found for this session.";
    return false;
  } catch (err) {
    state.sessionArtifactDetail = null;
    state.sessionArtifactsError = err instanceof Error ? err.message : String(err);
    return false;
  } finally {
    state.sessionArtifactDetailLoading = false;
  }
}

export async function saveSessionWorkspaceFile(
  state: SessionArtifactsState & {
    chatWorkspaceSelectedPath?: string | null;
    sessionArtifactEditContent?: string | null;
  },
): Promise<boolean> {
  const path = state.chatWorkspaceSelectedPath?.trim();
  if (!state.client || !state.connected || !path) {
    return false;
  }
  state.sessionArtifactWriteSaving = true;
  try {
    const scopeKey = state.filesScopeSessionKey?.trim() || state.sessionKey;
    const agentId = resolveAgentIdForSession(scopeKey);
    await state.client.request("agents.workspace.write", {
      agentId,
      sessionKey: scopeKey,
      path,
      content: state.sessionArtifactEditContent ?? "",
      ...(state.chatActiveProjectId ? { projectId: state.chatActiveProjectId } : {}),
    });
    await loadSessionArtifacts(state);
    return true;
  } catch (err) {
    state.sessionArtifactsError = String(err);
    return false;
  } finally {
    state.sessionArtifactWriteSaving = false;
  }
}
